import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import restaurantService from '../services/restaurantService'
import cuisineService from '../services/cuisineService'
import { Cuisine, RESTAURANT_TYPES } from '../types/restaurant'
import { useTranslation } from '../hooks/use-i18n'

interface CreateRestaurantModalProps {
  onClose: () => void
  onSuccess: () => void
}

interface CreateRestaurantForm {
  name: string
  restaurantType: string
}

const CreateRestaurantModal: React.FC<CreateRestaurantModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [cuisines, setCuisines] = useState<Cuisine[]>([])
  const [cuisinesLoading, setCuisinesLoading] = useState(true)
  const [selectedCuisineIds, setSelectedCuisineIds] = useState<string[]>([])
  const { register, handleSubmit, formState: { errors } } = useForm<CreateRestaurantForm>({
    defaultValues: {
      restaurantType: t('restaurant.type.casualDining'),
    }
  })

  useEffect(() => {
    const loadCuisines = async () => {
      try {
        setCuisinesLoading(true)
        const list = await cuisineService.getCuisines()
        setCuisines(list)
      } catch (err) {
        toast.error(t('toast.cuisinesLoadFailed'))
        setCuisines([])
      } finally {
        setCuisinesLoading(false)
      }
    }
    loadCuisines()
  }, [])

  const handleCuisineChange = (cuisineId: string, checked: boolean) => {
    if (checked) {
      setSelectedCuisineIds(prev => [...prev, cuisineId])
    } else {
      setSelectedCuisineIds(prev => prev.filter(id => id !== cuisineId))
    }
  }

  const onSubmit = async (data: CreateRestaurantForm) => {
    if (selectedCuisineIds.length === 0) {
      toast.error(t('toast.selectCuisineRequired'))
      return
    }

    try {
      setIsLoading(true)
      await restaurantService.createRestaurant({
        name: data.name,
        restaurantType: data.restaurantType,
        cuisineIds: selectedCuisineIds,
      })
      toast.success(t('toast.restaurantCreated'))
      await queryClient.invalidateQueries({ queryKey: ['restaurants'] })
      onSuccess()
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.restaurantCreateFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="blue-sky-card rounded-3xl p-8 w-full max-w-2xl mx-4 shadow-2xl animate-scale-in my-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-white">
            {t('restaurant.create.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Restaurant Name */}
          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">
              {t('restaurant.create.nameLabel')}
            </label>
            <input
              {...register('name', { required: t('restaurant.create.validation.nameRequired') })}
              type="text"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300"
              placeholder={t('restaurant.create.namePlaceholder')}
            />
            {errors.name && (
              <p className="mt-2 text-sm text-red-400">{errors.name.message}</p>
            )}
          </div>

          {/* Restaurant Type */}
          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">
              {t('restaurant.create.typeLabel')}
            </label>
            <select
              {...register('restaurantType', { required: t('restaurant.create.validation.typeRequired') })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300"
            >
              {RESTAURANT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {errors.restaurantType && (
              <p className="mt-2 text-sm text-red-400">{errors.restaurantType.message}</p>
            )}
          </div>

          {/* Cuisine Selection */}
          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">
              {t('restaurant.create.cuisinesLabel')}
            </label>
            {cuisinesLoading ? (
              <div className="py-8 text-center text-white/70">{t('restaurant.create.loadingCuisines')}</div>
            ) : (
              <>
                <div className="max-h-96 overflow-y-auto bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {cuisines.map((cuisine) => (
                      <label
                        key={cuisine.id}
                        className={`flex items-start p-3 rounded-lg transition-colors cursor-pointer ${
                          selectedCuisineIds.includes(cuisine.id)
                            ? 'bg-sky-500/20 border-2 border-sky-500'
                            : 'bg-white/5 border-2 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCuisineIds.includes(cuisine.id)}
                          onChange={(e) => handleCuisineChange(cuisine.id, e.target.checked)}
                          className="mt-1 mr-3 w-4 h-4 text-sky-500 bg-white/10 border-white/20 rounded focus:ring-sky-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-white block">
                            {cuisine.name}
                          </span>
                          {cuisine.description && (
                            <span className="text-xs text-white/70 mt-1 block">
                              {cuisine.description}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                {selectedCuisineIds.length === 0 && (
                  <p className="mt-2 text-sm text-yellow-400">
                    {t('restaurant.create.selectCuisineWarning')}
                  </p>
                )}
                {selectedCuisineIds.length > 0 && (
                  <p className="mt-2 text-sm text-white/70">
                    {t('restaurant.create.selectedCuisines', { count: String(selectedCuisineIds.length), label: selectedCuisineIds.length === 1 ? t('common.cuisineSingular') : t('common.cuisines') })}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-white/30 text-white rounded-xl hover:bg-white/10 transition-all duration-300 font-semibold"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || selectedCuisineIds.length === 0 || cuisinesLoading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl font-semibold"
            >
              {isLoading ? t('restaurant.create.submitting') : t('restaurant.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateRestaurantModal
